<template>
  <div>
    <v-data-table
      disable-pagination
      hide-default-footer
      :headers="headers"
      :items="displayData"
      :height="height"
      class="database-table overview"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="item.day" class="database-row color-alt">
            <td class="pa-2 overview-block overview-day-col">
              <span>{{ item.day }} ({{ item.weekday }})</span>
            </td>
            <td class="pa-2 overview-block overview-date-col">
              <span>{{ item.displayDate }}</span>
            </td>
            <td class="pa-2 overview-block overview-city-col">
              <span class="overview-city-text">{{ item.city }}</span>
            </td>
            <QuoteOverviewRow :suppliers="item.hotels" product-type="ROOM" />
            <QuoteOverviewRow :suppliers="item.restaurants" product-type="MEAL" />
            <QuoteOverviewRow :suppliers="item.parks" product-type="EXPENSE" />
            <QuoteOverviewRow :suppliers="item.excursions" product-type="EXCURSION" />
            <QuoteOverviewRow :suppliers="item.transport" product-type="TRANS" />
            <QuoteOverviewRow :suppliers="item.tour_guides" product-type="TOUR" />
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import format from "@/services/format";
import { PRODUCT_TYPES } from "@/config/TYPES";
import QuoteOverviewRow from "./_QuoteOverviewRow";
import quoteUtils from "@/modules/quotes/components/utils";

const DAY_ITEM_COLUMNS = {
  hotels: [PRODUCT_TYPES.ROOM],
  restaurants: [PRODUCT_TYPES.MEAL],
  excursions: [PRODUCT_TYPES.EXCURSION],
};

const LOGISTICS_COLUMNS = {
  transport: [PRODUCT_TYPES.TRANS],
  tour_guides: [PRODUCT_TYPES.TOUR, PRODUCT_TYPES.TOUR_LEADER],
};

export default {
  props: {
    height: [Number, String],
  },
  components: {
    QuoteOverviewRow,
  },
  data() {
    return {
      headers: [
        { text: "Day", value: "day" },
        { text: "Date", value: "date" },
        { text: "City", value: "city" },
        { text: "Hotels", value: "hotels" },
        { text: "Restaurants", value: "restaurants" },
        { text: "Parks", value: "parks" },
        { text: "Excursions", value: "excursions" },
        { text: "Transportation", value: "transport" },
        { text: "Tour Guides", value: "tour_guides" },
      ],
    };
  },
  computed: {
    quote() {
      return this.$store.getters["QuoteStore/active"];
    },
    displayData() {
      const quote = this.quote;
      if (!quote) return [];

      const costs = quote.costs || [];
      const logisticsCosts = quote.logisticsCosts || [];
      const period = quote.period || {};
      const supplierProductData = this.$store.getters["QuoteStore/supplierProductData"] || {};

      return costs.map((day, index) => {
        const dateInfo = _getDayDateInfo(period, index);
        const items = day.items || [];

        return {
          day: index + 1,
          date: dateInfo.date,
          weekday: dateInfo.weekday,
          displayDate: dateInfo.displayDate,
          city: quoteUtils.formatDayCities(day.cityStart, day.cityEnd, " to "),
          hotels: _extractSupplierEntries(items, DAY_ITEM_COLUMNS.hotels, supplierProductData),
          restaurants: _extractSupplierEntries(items, DAY_ITEM_COLUMNS.restaurants, supplierProductData),
          parks: _extractParkEntries(items, supplierProductData),
          excursions: _extractSupplierEntries(items, DAY_ITEM_COLUMNS.excursions, supplierProductData),
          transport: _extractLogisticsSupplierEntries(logisticsCosts, index, LOGISTICS_COLUMNS.transport),
          tour_guides: _extractLogisticsSupplierEntries(logisticsCosts, index, LOGISTICS_COLUMNS.tour_guides),
        };
      });
    },
  },
};

function _getDayDateInfo(period, index) {
  const { startYear, startMonth, startDay } = period || {};

  if (startYear == null || startMonth == null || startDay == null) {
    return {
      date: null,
      weekday: "—",
      displayDate: "",
    };
  }

  const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));
  const dayDate = new Date(startDate);
  dayDate.setDate(startDate.getDate() + index);

  const date = format.convertDate(dayDate);
  return {
    date,
    weekday: format.getWeekday(date),
    displayDate: format.formatDate(date, true),
  };
}

function _displayName(item) {
  return item?.supplier?.label || item?.product?.label || "TBD";
}

function _isParkSupplier(item, supplierProductData) {
  const supplierId = item.supplier?.value;
  if (!supplierId) return false;

  const supplier = supplierProductData[supplierId]?.supplier;
  if (!supplier) return false;

  return supplier.supplier_type === "PARK";
}

function _extractParkEntries(items, supplierProductData) {
  const groups = new Map();

  (items || []).forEach((item) => {
    if (!_isParkSupplier(item, supplierProductData)) return;
    if (item.productType === PRODUCT_TYPES.EXPENSE) return;
    if (item.productType === PRODUCT_TYPES.EXCURSION) return;

    const name = _displayName(item);
    const key = item.supplier?.value ?? name;

    if (!groups.has(key)) {
      groups.set(key, { key, name, items: [] });
    }

    groups.get(key).items.push(item);
  });

  return Array.from(groups.values());
}

function _extractSupplierEntries(items, productTypes, supplierProductData) {
  const groups = new Map();

  (items || []).forEach((item) => {
    if (!productTypes.includes(item.productType)) return;

    const name = _displayName(item);
    const key = item.supplier?.value ?? name;

    if (!groups.has(key)) {
      groups.set(key, { key, name, items: [] });
    }

    groups.get(key).items.push(item);
  });

  return Array.from(groups.values());
}

function _extractLogisticsSupplierEntries(logisticsCosts, dayIndex, productTypes) {
  const groups = new Map();

  (logisticsCosts || []).forEach((item) => {
    if (!productTypes.includes(item.productType)) return;

    const days = Array.isArray(item.days) ? item.days : [];
    if (!days.some((d) => Number(d) === dayIndex)) return;

    const name = _displayName(item);
    const key = item.supplier?.value ?? item.uuid ?? name;

    if (!groups.has(key)) {
      groups.set(key, { key, name, items: [] });
    }

    groups.get(key).items.push(item);
  });

  return Array.from(groups.values());
}
</script>

<style lang="scss">
.overview-block {
  font-size: 10px !important;
}

.overview.database-table .database-row:hover {
  cursor: default;
}

.overview-day-col {
  min-width: 48px;
  max-width: 56px;
  width: 56px;
  vertical-align: top;
  white-space: normal;
  line-height: 1.25;
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.overview-date-col {
  min-width: 36px;
  max-width: 42px;
  width: 42px;
  vertical-align: top;
  white-space: normal;
  line-height: 1.25;
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.overview-city-col {
  min-width: 120px;
  max-width: 180px;
  width: 14%;
  vertical-align: top;
}

.overview-city-text {
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.overview-product-type-cell {
  background-color: var(--quote-product-type-bg) !important;
}
</style>
